import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE } from "@common/helpers/transactions";
import { pluralizeWithCount } from "@common/helpers/string";
import { TaskType } from "@common/types/task";
import { useEntities } from "@hooks/useEntities";
import { useScope } from "@state/scopeContext.lib";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useOwnedCommandPalette } from "@components/commands/useOwnedCommandPalette";
import {
    bulkAddFieldReviews,
    bulkRevokeFieldReviews,
} from "@api/transaction.api";
import { bulkApproveAccrualReview } from "@api/accrual-review.api";
import { scheduleConflictSkippedMessage } from "@components/accounting/accrualReview/scheduleConflictSkippedMessage";
import { invalidateBillQueries } from "@api/bill.api";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { matchTransfer } from "@lib/transactions";
import { invalidateTransactionsForTransferMatchingQueries } from "@components/transactions/TransferMatching/useTransactionsForTransferMatching";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { TransactionColumn } from "@common/types/views/transactionViews";
import {
    TRANSACTIONS_PAGE_SIZE,
    invalidateTransactionsQueries,
} from "../useTransactionsQuery";
import { createTransactionsUrl } from "../lib/transactionsUrl";
import { PaginatedTransactionsDataContext } from "../PaginatedTransactionsTable.context";
import { useTransactionsBulkActionConditions } from "../useTransactionsBulkActionConditions";
import {
    getSuggestedMatchTransactionIds,
    getTransferMatchPairs,
    hasSuggestedBillMatch,
    hasSuggestedInvoiceMatch,
} from "../useTransactionsBulkActionConditions.helpers";
import { useBulkActionHandlers } from "../hooks/useBulkActionHandlers";
import { useBulkActions } from "./useBulkActions";
import { useBulkUnmatchInvoices } from "./useBulkUnmatchInvoices";
import {
    buildBulkFieldReviewRequests,
    reviewActionDropsFromFilter,
    submitBulkFieldReviewRequests,
} from "./buildBulkFieldReviewRequests";
import { BulkUpdateStatus, LOCKED_TRANSACTIONS_DISABLED_TOOLTIP } from "./lib";
import { useGustoReconciliationCouldBeApplied } from "./useGustoReconciliationCouldBeApplied";
import { useTransactionCommandOptions } from "./useTransactionCommandOptions";
import { useTransactionBulkModals } from "./useTransactionBulkModals";
import { buildBillableExpenseInvoicePrefill } from "./buildBillableExpenseInvoicePrefill";

interface UseTransactionBulkActionsParams {
    isEnabled?: boolean;
    /**
     * When provided, column-specific bulk command options (category, COA,
     * accrual COA, classes) are only included when the corresponding column ID
     * is present in this set. Leave undefined to include all options (default
     * behaviour used by the main transactions view).
     */
    visibleColumnIds?: Set<string>;
    bulkCommandsDirectory?: CommandsDirectory;
    transactionBulkCoaHeading?: string;
}

const INVALIDATE_DOCUMENT_QUERIES = {
    invoice: invalidateInvoiceQueries,
    bill: invalidateBillQueries,
} satisfies Record<"invoice" | "bill", () => void>;

export function useTransactionBulkActions({
    isEnabled = true,
    visibleColumnIds,
    bulkCommandsDirectory = CommandsDirectory.TRANSACTION_BULK,
    transactionBulkCoaHeading,
}: UseTransactionBulkActionsParams = {}) {
    const bulkActions = useBulkActions();
    const entities = useEntities();
    const { workspaceId } = useScope();
    const { openOverlay } = useOverlays();
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const { currentView, loadMore } = useContext(
        PaginatedTransactionsDataContext,
    );
    const conditions = useTransactionsBulkActionConditions();
    const invoiceOrBillLockUnlockedIds = useMemo(
        () =>
            conditions.invoiceOrBillLockPartition.unlockedTransactions.map(
                (t) => t.id,
            ),
        [conditions.invoiceOrBillLockPartition.unlockedTransactions],
    );
    const entityUnlockedIds = useMemo(
        () =>
            conditions.scheduleEntityLockPartition.unlockedTransactions.map(
                (t) => t.id,
            ),
        [conditions.scheduleEntityLockPartition.unlockedTransactions],
    );
    const accrualLedgerUnlockedIds = useMemo(() => {
        const scheduleUnlockedIds = new Set(
            conditions.scheduleLockPartition.unlockedTransactions.map(
                (t) => t.id,
            ),
        );
        return invoiceOrBillLockUnlockedIds.filter((id) =>
            scheduleUnlockedIds.has(id),
        );
    }, [
        invoiceOrBillLockUnlockedIds,
        conditions.scheduleLockPartition.unlockedTransactions,
    ]);
    const {
        bulkTransactionDeletion: isBulkDeletionEnabled,
        schedules: isSchedulesEnabled,
    } = useTypedFlags();
    const { toast, error: toastError } = useToaster();
    const { enabled: isGlFirst } = useIsGlFirst();

    const {
        switchingToBusinessCategory,
        onBusinessSelected,
        hideBusinessSelection,
    } = useBulkActionHandlers();

    const { registerOpenOverride } = useCommands();

    const {
        handleCreateCounterpartyClosed: onCounterpartyCreated,
        handleCreateCustomerClosed: onCustomerCreated,
        ...modals
    } = useTransactionBulkModals({
        unlockedTransactionIds: invoiceOrBillLockUnlockedIds,
    });

    const isBusy =
        bulkActions.updateDetails?.status === BulkUpdateStatus.UPDATING;
    const isFieldLocked = isBusy || conditions.areAnySelectedTransactionsLocked;
    const lockedSelectionTooltip = conditions.areAnySelectedTransactionsLocked
        ? LOCKED_TRANSACTIONS_DISABLED_TOOLTIP
        : undefined;
    const isUnsplitLocked =
        isFieldLocked || conditions.areAnySelectedMatchedToInvoiceOrBill;
    const unsplitLockedTooltip = conditions.areAnySelectedMatchedToInvoiceOrBill
        ? MATCHED_INVOICE_OR_BILL_UNSPLIT_MESSAGE
        : lockedSelectionTooltip;

    const gustoReconciliationCouldBeApplied =
        useGustoReconciliationCouldBeApplied({
            selectedArray: bulkActions.selectedArray,
        });

    const {
        entityOptions,
        counterpartyOptions,
        customerOptions,
        categoryOptions,
        coaOptions,
        accrualCoaOptions,
        scheduleOptions,
        scheduleConfirmModal,
        classTree,
        classesMap,
        applyClasses,
        showCreateCounterparty,
        setShowCreateCounterparty,
        showCreateCustomer,
        setShowCreateCustomer,
    } = useTransactionCommandOptions({
        isFieldLocked,
        isSchedulesEnabled,
        unlockedTransactionIds: invoiceOrBillLockUnlockedIds,
        accrualLedgerUnlockedTransactionIds: accrualLedgerUnlockedIds,
        entityUnlockedTransactionIds: entityUnlockedIds,
    });

    const copyToClipboard = useCopyToClipboard({
        successMessage: "Link copied to clipboard",
    });

    const handleCopyLink = useCallback(() => {
        const selectedTransactionIds = Object.keys(bulkActions.selected).map(
            (id) => parseInt(id, 10),
        );
        const shareableUrl = `${window.location.origin}${createTransactionsUrl({
            transactionIds: selectedTransactionIds,
        })}`;
        copyToClipboard(shareableUrl);
    }, [bulkActions.selected, copyToClipboard]);

    const handleInvoiceToCustomer = useCallback(() => {
        const prefill = buildBillableExpenseInvoicePrefill(
            bulkActions.selectedArray,
        );
        if (!prefill) {
            return;
        }
        openOverlay("nativeInvoiceDrawer", { createPrefill: prefill });
    }, [bulkActions.selectedArray, openOverlay]);

    const handleCreateTask = useCallback(() => {
        // Every branch below opens a create form the server can only refuse
        // on save (PRO-11345).
        if (refuseIfViewOnly()) {
            return;
        }
        const entityIdsFilter = entities.map((e) => e.id);

        // When "select all" is active the selection is defined by the current
        // filters, not by the loaded rows in `selectedArray`. Routing through
        // the filter-based path below keeps per-entity task views scoped to the
        // full filtered set instead of the (possibly partial) loaded rows.
        if (
            bulkActions.selectedArray.length > 0 &&
            !bulkActions.hasSelectedAll
        ) {
            const entityIdsTask = [
                ...new Set(bulkActions.selectedArray.map((t) => t.entity.id)),
            ];
            const transactionsTask = {
                transactionsCount: bulkActions.selectedCount,
                transactions: bulkActions.selectedArray,
            };

            if (entityIdsTask.length > 1) {
                // Multiple entities selected — group transaction IDs by entity
                const transactionsByEntity = new Map<number, number[]>();
                for (const t of bulkActions.selectedArray) {
                    const list = transactionsByEntity.get(t.entity.id) ?? [];
                    list.push(t.id);
                    transactionsByEntity.set(t.entity.id, list);
                }

                const baseViewConfig = {
                    ...currentView,
                    filters: {
                        entityIds: entityIdsFilter,
                    },
                };

                openOverlay("createTask", {
                    type: TaskType.TRANSACTIONS,
                    viewConfig: baseViewConfig,
                    transactionsTask,
                    initialValues: { entityIds: entityIdsTask },
                    perEntityTask: {
                        entityGroups: entityIdsTask.map((entityId) => ({
                            entityId,
                            transactionIds:
                                transactionsByEntity.get(entityId) ?? [],
                        })),
                        baseViewConfig,
                    },
                });
                return;
            }

            openOverlay("createTask", {
                type: TaskType.TRANSACTIONS,
                viewConfig: {
                    ...currentView,
                    filters: {
                        entityIds: entityIdsFilter,
                        ...(Object.keys(bulkActions.selected).length > 0
                            ? {}
                            : currentView.filters),
                        transactionIds: Object.values(bulkActions.selected).map(
                            (t) => t.id,
                        ),
                    },
                },
                transactionsTask,
                initialValues: { entityIds: entityIdsTask },
            });
            return;
        }

        const entityIds = [...new Set(currentView.filters.entityIds ?? [])];
        const transactionsTask = {
            transactionsCount: bulkActions.totalTransactions,
        };

        if (entityIds.length > 1) {
            // No selection, but multiple entities in filter — create one task per entity
            openOverlay("createTask", {
                type: TaskType.TRANSACTIONS,
                viewConfig: currentView,
                transactionsTask,
                initialValues: { entityIds },
                perEntityTask: {
                    entityGroups: entityIds.map((entityId) => ({ entityId })),
                    baseViewConfig: currentView,
                },
            });
            return;
        }

        openOverlay("createTask", {
            type: TaskType.TRANSACTIONS,
            viewConfig: currentView,
            transactionsTask,
            initialValues: { entityIds },
        });
    }, [bulkActions, currentView, entities, openOverlay, refuseIfViewOnly]);

    const handleCreateCounterpartyClosed = useCallback(
        async (
            creationResponse?: Parameters<typeof onCounterpartyCreated>[0],
        ) => {
            setShowCreateCounterparty(false);
            await onCounterpartyCreated(creationResponse);
        },
        [onCounterpartyCreated, setShowCreateCounterparty],
    );

    const handleCreateCustomerClosed = useCallback(
        async (creationResponse?: Parameters<typeof onCustomerCreated>[0]) => {
            setShowCreateCustomer(false);
            await onCustomerCreated(creationResponse);
        },
        [onCustomerCreated, setShowCreateCustomer],
    );

    const handleMarkReviewedForFields = useCallback(
        async (fields: TransactionReviewField[]) => {
            const requests = buildBulkFieldReviewRequests({
                fields,
                transactionIds: Object.values(bulkActions.selected).map(
                    (t) => t.id,
                ),
                filtersSelection: bulkActions.filtersSelection,
                workspaceId,
                fallbackEntityIds: entities.map((entity) => entity.id),
                transactionCount: bulkActions.selectedCount,
                dropsFromFilter: reviewActionDropsFromFilter(
                    bulkActions.filtersSelection,
                    fields,
                    "add",
                ),
            });
            if (requests.length === 0) {
                return;
            }
            try {
                const count = await submitBulkFieldReviewRequests(
                    requests,
                    bulkAddFieldReviews,
                );
                bulkActions.clearSelection();
                await invalidateTransactionsQueries();
                toast(
                    `Marked ${pluralizeWithCount("field", count)} as reviewed`,
                );
            } catch {
                toastError("Failed to mark transactions as reviewed");
            }
        },
        [bulkActions, entities, toast, toastError, workspaceId],
    );

    const handleUnreviewForFields = useCallback(
        async (fields: TransactionReviewField[]) => {
            const requests = buildBulkFieldReviewRequests({
                fields,
                transactionIds: Object.values(bulkActions.selected).map(
                    (t) => t.id,
                ),
                filtersSelection: bulkActions.filtersSelection,
                workspaceId,
                fallbackEntityIds: entities.map((entity) => entity.id),
                transactionCount: bulkActions.selectedCount,
                dropsFromFilter: reviewActionDropsFromFilter(
                    bulkActions.filtersSelection,
                    fields,
                    "revoke",
                ),
            });
            if (requests.length === 0) {
                return;
            }
            try {
                const count = await submitBulkFieldReviewRequests(
                    requests,
                    bulkRevokeFieldReviews,
                );
                bulkActions.clearSelection();
                await invalidateTransactionsQueries();
                toast(`Removed ${pluralizeWithCount("review", count)}`);
            } catch {
                toastError("Failed to remove reviews from transactions");
            }
        },
        [bulkActions, entities, toast, toastError, workspaceId],
    );

    const handleMatchTransactions = useCallback(async () => {
        const pairs = getTransferMatchPairs(bulkActions.selectedArray);
        if (!pairs) {
            return;
        }

        // Each pair is its own request — match them sequentially so a large
        // selection does not fan out into parallel calls. Track the legs that
        // actually committed so a mid-loop failure can report partial success
        // and leave the unmatched pairs selected for a direct retry, instead
        // of clearing everything and reporting a blanket failure.
        const matchedTransactions: typeof bulkActions.selectedArray = [];
        let didFail = false;
        for (const [origin, target] of pairs) {
            try {
                await matchTransfer(origin.id, target.id);
                matchedTransactions.push(origin, target);
            } catch {
                didFail = true;
                break;
            }
        }

        const matchedPairCount = matchedTransactions.length / 2;
        if (didFail) {
            if (matchedPairCount > 0) {
                bulkActions.deselect(matchedTransactions);
                toastError(
                    `Matched ${pluralizeWithCount("pair", matchedPairCount)}, but couldn't match the rest`,
                );
            } else {
                toastError("Failed to match transactions");
            }
        } else {
            bulkActions.clearSelection();
            toast("Transactions matched");
        }

        await Promise.all([
            invalidateTransactionsQueries(),
            invalidateTransactionsForTransferMatchingQueries(),
        ]);
    }, [bulkActions, toast, toastError]);

    const acceptSuggestedMatches = useCallback(
        async (kind: "invoice" | "bill", transactionIds: number[]) => {
            if (!workspaceId || transactionIds.length === 0) {
                return;
            }
            const matchLabel = `${kind} match`;

            try {
                const response = await bulkApproveAccrualReview({
                    workspaceId,
                    body: { transactionIds },
                });
                const skippedCount =
                    response.skippedReviewCount +
                    response.skippedAlreadyAcceptedCount;

                if (response.approvedCount > 0) {
                    toast(
                        `Confirmed ${pluralizeWithCount(matchLabel, response.approvedCount)}`,
                    );
                }
                if (skippedCount > 0) {
                    toastError(
                        `${pluralizeWithCount("transaction", skippedCount)} skipped (require review or already confirmed)`,
                    );
                }
                if (response.skippedScheduleConflictCount > 0) {
                    toastError(
                        scheduleConflictSkippedMessage(
                            response.skippedScheduleConflictCount,
                        ),
                    );
                }
                if (response.failedCount > 0) {
                    toastError(
                        `${pluralizeWithCount(matchLabel, response.failedCount)} couldn't be confirmed`,
                    );
                }
                if (
                    response.approvedCount === 0 &&
                    skippedCount === 0 &&
                    response.skippedScheduleConflictCount === 0 &&
                    response.failedCount === 0
                ) {
                    toast(`No ${kind} matches to confirm`);
                }
                if (response.failedCount === 0) {
                    bulkActions.clearSelection();
                }
                if (response.approvedCount > 0) {
                    INVALIDATE_DOCUMENT_QUERIES[kind]();
                }
                await invalidateTransactionsQueries();
            } catch {
                toastError(`Failed to confirm ${kind} matches`);
            }
        },
        [bulkActions, toast, toastError, workspaceId],
    );

    const handleAcceptInvoiceMatches = useCallback(
        () =>
            acceptSuggestedMatches(
                "invoice",
                getSuggestedMatchTransactionIds({
                    selectedArray: bulkActions.selectedArray,
                    hasSuggestedMatch: hasSuggestedInvoiceMatch,
                }),
            ),
        [acceptSuggestedMatches, bulkActions.selectedArray],
    );

    const handleAcceptBillMatches = useCallback(
        () =>
            acceptSuggestedMatches(
                "bill",
                getSuggestedMatchTransactionIds({
                    selectedArray: bulkActions.selectedArray,
                    hasSuggestedMatch: hasSuggestedBillMatch,
                }),
            ),
        [acceptSuggestedMatches, bulkActions.selectedArray],
    );

    const handleUnmatchInvoices = useBulkUnmatchInvoices();

    const handleRemoveMemo = useCallback(async () => {
        try {
            await bulkActions.updateSelected({ update: "memo", value: "" });
        } catch {
            toastError("Failed to clear memo");
        }
    }, [bulkActions, toastError]);

    const handleRemoveClasses = useCallback(async () => {
        try {
            await bulkActions.updateSelected(
                { update: "classes", value: [] },
                { targetTransactionIds: invoiceOrBillLockUnlockedIds },
            );
        } catch {
            toastError("Failed to remove classes");
        }
    }, [bulkActions, toastError, invoiceOrBillLockUnlockedIds]);

    const handleExcludeFrom1099 = useCallback(async () => {
        try {
            await bulkActions.updateSelected({
                update: "excludeFrom1099",
                value: true,
            });
        } catch {
            toastError("Failed to exclude transactions from 1099");
        }
    }, [bulkActions, toastError]);

    const handleIncludeIn1099 = useCallback(async () => {
        try {
            await bulkActions.updateSelected({
                update: "excludeFrom1099",
                value: false,
            });
        } catch {
            toastError("Failed to include transactions in 1099");
        }
    }, [bulkActions, toastError]);

    const isColumnVisible = useCallback(
        (columnId: TransactionColumn) =>
            !visibleColumnIds || visibleColumnIds.has(columnId),
        [visibleColumnIds],
    );

    const bulkCommandOptions = useMemo(() => {
        const isClassBulkActionEnabled =
            isColumnVisible(TransactionColumn.CLASSES) &&
            conditions.areClassesEnabled &&
            !conditions.invoiceOrBillLockPartition.allLocked;
        const isCounterpartyBulkActionEnabled =
            isColumnVisible(TransactionColumn.COUNTERPARTY) &&
            !conditions.invoiceOrBillLockPartition.allLocked;
        const isInvoiceOrBillLedgerBulkActionEnabled =
            !conditions.invoiceOrBillLockPartition.allLocked;
        const transactionsWithExistingClassCount =
            conditions.invoiceOrBillLockPartition.unlockedTransactions.filter(
                (transaction) =>
                    transaction.classAssignments &&
                    transaction.classAssignments.length > 0,
            ).length;

        return {
            transactionBulkCoaHeading,
            transactionBulkEntityOptions:
                isColumnVisible(TransactionColumn.ENTITY) &&
                !conditions.scheduleEntityLockPartition.allLocked
                    ? entityOptions
                    : undefined,
            transactionBulkCounterpartyOptions: isCounterpartyBulkActionEnabled
                ? counterpartyOptions
                : undefined,
            transactionBulkCustomerOptions:
                isColumnVisible(TransactionColumn.CUSTOMER) &&
                !conditions.hasInvoicedTransactionsInSelection
                    ? customerOptions
                    : undefined,
            transactionBulkCategoryOptions:
                isColumnVisible(TransactionColumn.CATEGORY) && !isGlFirst
                    ? categoryOptions
                    : undefined,
            transactionBulkCoaOptions:
                isColumnVisible(TransactionColumn.LEDGER) &&
                conditions.shouldShowCoaButton &&
                isInvoiceOrBillLedgerBulkActionEnabled
                    ? coaOptions
                    : undefined,
            transactionBulkAccrualCoaOptions:
                isColumnVisible(TransactionColumn.ACCRUAL_LEDGER) &&
                conditions.shouldShowAccrualCoaButton &&
                isInvoiceOrBillLedgerBulkActionEnabled &&
                (bulkActions.hasSelectedAll ||
                    accrualLedgerUnlockedIds.length > 0)
                    ? accrualCoaOptions
                    : undefined,
            transactionBulkScheduleOptions:
                isColumnVisible(TransactionColumn.SCHEDULE) &&
                isSchedulesEnabled &&
                conditions.shouldShowSetScheduleButton
                    ? scheduleOptions
                    : undefined,
            transactionBulkClassTree: isClassBulkActionEnabled
                ? classTree
                : undefined,
            transactionBulkClassesMap: isClassBulkActionEnabled
                ? classesMap
                : undefined,
            transactionApplyClasses: isClassBulkActionEnabled
                ? applyClasses
                : undefined,
            hasExistingClassAssignments: isClassBulkActionEnabled
                ? transactionsWithExistingClassCount > 0 ||
                  bulkActions.hasSelectedAll
                : undefined,
            transactionsWithExistingClassCount: isClassBulkActionEnabled
                ? transactionsWithExistingClassCount
                : undefined,
            transactionsLockedByInvoiceOrBillCount:
                conditions.invoiceOrBillLockPartition.lockedCount,
            // Rows locked only by a schedule; rows the invoice/bill lock
            // already excludes are counted once, in its own suffix.
            transactionsLockedByScheduleCount:
                invoiceOrBillLockUnlockedIds.length -
                accrualLedgerUnlockedIds.length,
            transactionsLockedByScheduleEntityCount:
                conditions.scheduleEntityLockPartition.lockedCount,
            isTransactionBulkFieldLocked: isFieldLocked,
            transactionBulkFieldLockedTooltip: lockedSelectionTooltip,
            isTransactionBulkUnsplitLocked: isUnsplitLocked,
            transactionBulkUnsplitLockedTooltip: unsplitLockedTooltip,
            selectedTransactionCount: bulkActions.selectedCount,
            onTransactionBulkSetMemo: isColumnVisible(TransactionColumn.NOTE)
                ? () => modals.setShouldShowMemo(true)
                : undefined,
            onTransactionBulkRemoveMemo:
                isColumnVisible(TransactionColumn.NOTE) &&
                conditions.shouldShowRemoveMemoButton
                    ? handleRemoveMemo
                    : undefined,
            onTransactionBulkCopyLink: conditions.shouldShowCopyLinkButton
                ? handleCopyLink
                : undefined,
            onTransactionBulkCreateTask: handleCreateTask,
            onTransactionBulkUnmatchAll:
                isColumnVisible(TransactionColumn.TRANSFER_MATCH) &&
                conditions.shouldShowUnmatchAllButton
                    ? () => modals.setShouldShowUnmatch(true)
                    : undefined,
            onTransactionBulkMatch:
                isColumnVisible(TransactionColumn.TRANSFER_MATCH) &&
                conditions.shouldShowMatchButton
                    ? handleMatchTransactions
                    : undefined,
            onTransactionBulkUnmatchInvoices:
                conditions.shouldShowUnmatchInvoicesButton
                    ? handleUnmatchInvoices
                    : undefined,
            onTransactionBulkAcceptInvoiceMatches:
                conditions.shouldShowAcceptInvoiceMatchesButton
                    ? handleAcceptInvoiceMatches
                    : undefined,
            onTransactionBulkAcceptBillMatches:
                conditions.shouldShowAcceptBillMatchesButton
                    ? handleAcceptBillMatches
                    : undefined,
            onTransactionBulkUnsplitAll:
                isColumnVisible(TransactionColumn.SPLIT) &&
                conditions.shouldShowUnsplitAllButton
                    ? () => modals.setShouldShowUnsplit(true)
                    : undefined,
            onTransactionBulkRevertGL:
                isColumnVisible(TransactionColumn.LEDGER) &&
                conditions.shouldShowRevertGLButton &&
                isInvoiceOrBillLedgerBulkActionEnabled
                    ? () => modals.setShouldShowRevertGL(true)
                    : undefined,
            onTransactionBulkRemoveCounterparty:
                isColumnVisible(TransactionColumn.COUNTERPARTY) &&
                conditions.shouldShowRemoveCounterpartyButton
                    ? () => modals.setShouldShowRemoveCounterparty(true)
                    : undefined,
            onTransactionBulkRemoveCustomer:
                isColumnVisible(TransactionColumn.CUSTOMER) &&
                conditions.shouldShowRemoveCustomerButton
                    ? () => modals.setShouldShowRemoveCustomer(true)
                    : undefined,
            onTransactionBulkInvoiceToCustomer:
                conditions.shouldShowInvoiceToCustomerButton
                    ? handleInvoiceToCustomer
                    : undefined,
            onTransactionBulkRemoveClasses:
                isColumnVisible(TransactionColumn.CLASSES) &&
                conditions.shouldShowRemoveClassesButton
                    ? handleRemoveClasses
                    : undefined,
            onTransactionBulkDelete:
                isBulkDeletionEnabled && !bulkActions.hasSelectedAll
                    ? () => modals.setShouldShowDelete(true)
                    : undefined,
            onTransactionBulkReconcileGusto: gustoReconciliationCouldBeApplied
                ? () => modals.setShouldShowGusto(true)
                : undefined,
            onTransactionBulkMarkReviewedForFields: isColumnVisible(
                TransactionColumn.REVIEW,
            )
                ? handleMarkReviewedForFields
                : undefined,
            onTransactionBulkUnreviewForFields: isColumnVisible(
                TransactionColumn.REVIEW,
            )
                ? handleUnreviewForFields
                : undefined,
            onTransactionBulkExclude1099: conditions.shouldShowExclude1099Button
                ? handleExcludeFrom1099
                : undefined,
            onTransactionBulkInclude1099: conditions.shouldShowInclude1099Button
                ? handleIncludeIn1099
                : undefined,
        };
    }, [
        entityOptions,
        counterpartyOptions,
        customerOptions,
        categoryOptions,
        isGlFirst,
        coaOptions,
        accrualCoaOptions,
        scheduleOptions,
        classTree,
        classesMap,
        applyClasses,
        isSchedulesEnabled,
        isFieldLocked,
        lockedSelectionTooltip,
        isUnsplitLocked,
        unsplitLockedTooltip,
        bulkActions.selectedCount,
        conditions,
        invoiceOrBillLockUnlockedIds,
        accrualLedgerUnlockedIds,
        isBulkDeletionEnabled,
        bulkActions.hasSelectedAll,
        gustoReconciliationCouldBeApplied,
        handleCopyLink,
        handleCreateTask,
        modals,
        handleMarkReviewedForFields,
        handleUnreviewForFields,
        handleRemoveMemo,
        handleRemoveClasses,
        handleExcludeFrom1099,
        handleIncludeIn1099,
        handleInvoiceToCustomer,
        handleMatchTransactions,
        handleUnmatchInvoices,
        handleAcceptInvoiceMatches,
        handleAcceptBillMatches,
        isColumnVisible,
        transactionBulkCoaHeading,
    ]);

    const viewOnlyCommandOptions = useMemo(
        () => ({
            selectedTransactionCount: bulkActions.selectedCount,
            onTransactionBulkCopyLink: conditions.shouldShowCopyLinkButton
                ? handleCopyLink
                : undefined,
        }),
        [
            bulkActions.selectedCount,
            conditions.shouldShowCopyLinkButton,
            handleCopyLink,
        ],
    );

    const paletteOptionsByKind = useMemo(
        () => ({
            bulk: bulkCommandOptions,
            viewOnly: viewOnlyCommandOptions,
            entity: {
                transactionBulkEntityOptions: conditions
                    .scheduleEntityLockPartition.allLocked
                    ? undefined
                    : entityOptions,
                selectedTransactionCount: bulkActions.selectedCount,
                transactionsLockedByScheduleEntityCount:
                    conditions.scheduleEntityLockPartition.lockedCount,
            },
            counterparty: {
                transactionBulkCounterpartyOptions: counterpartyOptions,
                selectedTransactionCount: bulkActions.selectedCount,
                transactionsLockedByInvoiceOrBillCount:
                    conditions.invoiceOrBillLockPartition.lockedCount,
            },
            customer: {
                transactionBulkCustomerOptions: customerOptions,
                selectedTransactionCount: bulkActions.selectedCount,
            },
        }),
        [
            bulkCommandOptions,
            viewOnlyCommandOptions,
            entityOptions,
            counterpartyOptions,
            customerOptions,
            bulkActions.selectedCount,
            conditions.invoiceOrBillLockPartition.lockedCount,
            conditions.scheduleEntityLockPartition.lockedCount,
            conditions.scheduleEntityLockPartition.allLocked,
        ],
    );

    const { openPalette } = useOwnedCommandPalette({
        optionsByKind: paletteOptionsByKind,
        selectedCount: bulkActions.selectedCount,
    });

    const handleOpenBulkActions = useCallback(() => {
        openPalette(bulkCommandsDirectory, isViewOnly ? "viewOnly" : "bulk");
    }, [openPalette, bulkCommandsDirectory, isViewOnly]);

    const handleSetCategoryClick = useCallback(() => {
        openPalette(CommandsDirectory.TRANSACTION_BULK_CATEGORY, "bulk");
    }, [openPalette]);

    const handleSetCoaClick = useCallback(() => {
        openPalette(CommandsDirectory.TRANSACTION_BULK_COA, "bulk");
    }, [openPalette]);

    const handleSetAccrualCoaClick = useCallback(() => {
        openPalette(CommandsDirectory.TRANSACTION_BULK_ACCRUAL_COA, "bulk");
    }, [openPalette]);

    const handleSetEntityClick = useCallback(() => {
        if (!bulkActions.selectedCount) {
            return;
        }
        openPalette(CommandsDirectory.TRANSACTION_BULK_ENTITY, "entity");
    }, [openPalette, bulkActions.selectedCount]);

    const handleSetCounterpartyClick = useCallback(() => {
        if (!bulkActions.selectedCount) {
            return;
        }
        openPalette(
            CommandsDirectory.TRANSACTION_BULK_COUNTERPARTY,
            "counterparty",
        );
    }, [openPalette, bulkActions.selectedCount]);

    const handleSetCustomerClick = useCallback(() => {
        if (!bulkActions.selectedCount) {
            return;
        }
        openPalette(CommandsDirectory.TRANSACTION_BULK_CUSTOMER, "customer");
    }, [openPalette, bulkActions.selectedCount]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (isEnabled && bulkActions.selectedCount > 0) {
            return registerOpenOverride(() =>
                handleOpenBulkActionsRef.current(),
            );
        }
    }, [isEnabled, bulkActions.selectedCount, registerOpenOverride]);

    return {
        selectedCount: bulkActions.selectedCount,
        hasSelectedAll: bulkActions.hasSelectedAll,
        filtersSelection: bulkActions.filtersSelection,
        totalTransactions: bulkActions.totalTransactions,
        clearSelection: bulkActions.clearSelection,
        selectAll: bulkActions.selectAll,
        selectedArray: bulkActions.selectedArray,
        isBusy,
        isFieldLocked,
        lockedSelectionTooltip,
        showSelectAll:
            !bulkActions.hasSelectedAll &&
            (loadMore
                ? loadMore.hasMore || loadMore.isTruncated
                : bulkActions.totalTransactions > TRANSACTIONS_PAGE_SIZE),

        handleSetEntityClick,
        handleSetCounterpartyClick,
        handleSetCustomerClick,
        handleSetCategoryClick,
        handleSetCoaClick,
        handleSetAccrualCoaClick,
        handleCreateTask,
        handleOpenBulkActions,
        shouldShowCoaButton: conditions.shouldShowCoaButton,
        hasAccrualCoaOptions:
            (bulkCommandOptions.transactionBulkAccrualCoaOptions?.length ?? 0) >
            0,

        switchingToBusinessCategory,
        onBusinessSelected,
        hideBusinessSelection,

        ...modals,
        scheduleConfirmModal,
        showCreateCounterparty,
        setShowCreateCounterparty,
        handleCreateCounterpartyClosed,
        showCreateCustomer,
        setShowCreateCustomer,
        handleCreateCustomerClosed,
    };
}
